import { formatAggregatedName } from "@/utils/formatUtils";
import t from "../../app/i18n";
import { ContactType, Country, MarketingConsent } from "../../common/enums";
import { FieldConstraintViolation } from "../../common/types";
import { ClientAttachmentType, ClientFormType, ClientType } from "./enums";
import type {
  Client,
  ClientAgreement,
  ClientAttachment,
  ClientBase,
  CreateUpdateClient,
  CreateUpdateClientAgreement,
  CreateUpdateContractClient,
  CreateUpdateLegalClient,
  CreateUpdateNaturalClient,
  CreateUpdateSelfEmployedClient,
  LegalClient,
  NaturalClient,
  SelfEmployedClient,
} from "./types";

export const createClientAggregatedName = (client: Partial<Client>) => {
  switch (client.type) {
    case ClientType.NATURAL:
      return createClientAggregatedNaturalName(client);
    case ClientType.SELF_EMPLOYED:
      return (client as SelfEmployedClient).companyName;
    case ClientType.LEGAL:
      return (client as LegalClient).companyName;
    default:
      return "";
  }
};

export const createClientAggregatedNaturalName = (client: Partial<Client>) => {
  const naturalClient = client as NaturalClient;
  return client.type !== ClientType.LEGAL
    ? formatAggregatedName({
        academicDegree: naturalClient.academicDegree,
        firstName: naturalClient.firstName,
        lastName: naturalClient.lastName,
        academicDegreeAfter: naturalClient.academicDegreeAfter,
      })
    : "";
};

export const resolveClientIdentifier = (client: Partial<Client>) => {
  switch (client.type) {
    case ClientType.NATURAL:
      return (client as NaturalClient).personalIdentificationNumber;
    case ClientType.SELF_EMPLOYED:
      return (client as SelfEmployedClient).companyRegistrationNumber;
    case ClientType.LEGAL:
      return (client as LegalClient).companyRegistrationNumber;
    default:
      return "";
  }
};

export const resolveClientAddressLabel = (type: ClientType, nationality?: Country) => {
  switch (type) {
    case ClientType.NATURAL:
      return nationality === Country.SVK ? t("client.attrs.addressLong") : t("client.attrs.addressLongForeigner");
    case ClientType.SELF_EMPLOYED:
      return t("client.attrs.addressLongFOP");
    case ClientType.LEGAL:
      return t("client.attrs.addressLongPO");
    default:
      return "";
  }
};

/**
 * @deprecated - use getCreateUpdateContractClientFromClient
 */
export const clientToCreateUpdateContractClient = (
  client: Partial<Client> | CreateUpdateClient,
): CreateUpdateContractClient => {
  if (isPartialClientObject(client)) {
    const { id, aggregatedName, identifier, createdAt, updatedAt, managedBy, historyRecords, managedById, ...data } =
      client;

    let clientData = { managedById: managedBy?.id ?? managedById, ...data } as CreateUpdateClient;

    if (clientData.type === ClientType.LEGAL) {
      clientData = { ...clientData, representatives: [] };
    }

    return { id: client.id, client: clientData, identifier: client.identifier ?? "" };
  }

  return {
    client: client,
    identifier: "identifier" in client ? (client.identifier as string) : "",
    id: "id" in client ? (client.id as string) : undefined,
  };
};

export const createUpdateContractClientToClient = (createUpdateClient?: CreateUpdateContractClient) => {
  if (createUpdateClient) {
    let client: Partial<Omit<Client, "representatives">> = {
      ...createUpdateClient.client,
      id: createUpdateClient.id,
    };
    const identifier = resolveClientIdentifier(client);
    const aggregatedName = createClientAggregatedName(client);

    client = { ...client, identifier, aggregatedName };

    if (client.type === ClientType.SELF_EMPLOYED) {
      const aggregatedNaturalName = createClientAggregatedNaturalName(client);
      client = { ...client, aggregatedNaturalName } as Partial<SelfEmployedClient>;
    }

    return client;
  }

  return undefined;
};

export const processClientFormValues = (formValues: CreateUpdateClient): CreateUpdateClient => {
  const processedValues = { ...formValues };

  processedValues.correspondenceAddress = Object.values(processedValues.correspondenceAddress ?? {}).some(
    (data) => data !== undefined,
  )
    ? processedValues.correspondenceAddress
    : undefined;

  processedValues.contacts =
    processedValues.contacts?.map((contact) => ({
      ...contact,
      marketingConsent: contact.type !== ContactType.OTHER ? contact.marketingConsent : MarketingConsent.NOT_GRANTED,
      marketingConsentStartDate: contact.type !== ContactType.OTHER ? contact.marketingConsentStartDate : undefined,
      marketingConsentEndDate: contact.type !== ContactType.OTHER ? contact.marketingConsentEndDate : undefined,
    })) || [];

  if (processedValues.type === ClientType.LEGAL) {
    (processedValues as CreateUpdateLegalClient).representatives = [];
  }

  return processedValues;
};

export const processClientsDataViolations = <T>(
  clientsIndicesMap: Map<T, number>,
  pathPrefix: string,
  violations?: FieldConstraintViolation[],
): Map<T, FieldConstraintViolation[]> => {
  const violationsMap = new Map<T, FieldConstraintViolation[]>();

  if (violations) {
    violations
      .filter((violation) => violation.fieldPath.startsWith(pathPrefix))
      .forEach((violation) => {
        for (const [type, index] of clientsIndicesMap) {
          if (
            index ===
            parseInt(
              violation.fieldPath.substring(violation.fieldPath.indexOf("[") + 1, violation.fieldPath.indexOf("]")),
            )
          ) {
            const processedViolation = {
              ...violation,
              fieldPath: violation.fieldPath.substring(pathPrefix.length + 11, violation.fieldPath.length),
            };
            violationsMap.set(
              type,
              violationsMap.has(type)
                ? [...(violationsMap.get(type) as FieldConstraintViolation[]), processedViolation]
                : [processedViolation],
            );
            break;
          }
        }
      });
  }

  return violationsMap;
};

export const isNaturalClient = (client: NaturalClient | SelfEmployedClient | LegalClient): client is NaturalClient => {
  return client.type === ClientType.NATURAL;
};

export const isSelfEmployedClient = (
  client: NaturalClient | SelfEmployedClient | LegalClient,
): client is SelfEmployedClient => {
  return client.type === ClientType.SELF_EMPLOYED;
};

export const isLegalClient = (client: NaturalClient | SelfEmployedClient | LegalClient): client is LegalClient => {
  return client.type === ClientType.LEGAL;
};

export const getCreateUpdateClientFromClient = (client: Client) => {
  if (isNaturalClient(client)) {
    return { ...client, managedById: client.managedBy?.id ?? client.managedById } as CreateUpdateNaturalClient;
  } else if (isSelfEmployedClient(client)) {
    return { ...client, managedById: client.managedBy?.id ?? client.managedById } as CreateUpdateSelfEmployedClient;
  } else if (isLegalClient(client)) {
    return {
      ...client,
      managedById: client.managedBy?.id ?? client.managedById,
      representatives: [
        ...(client.representatives?.map((clientRepresentative) => ({
          ...clientRepresentative,
          representativeId: clientRepresentative.representative.id,
        })) ?? []),
      ],
    } as CreateUpdateLegalClient;
  }

  return client;
};

export const getCreateUpdateContractClientFromClient = (client: Client): CreateUpdateContractClient => {
  return {
    id: client.id,
    client: { ...getCreateUpdateClientFromClient(client) },
    identifier: client.identifier,
  };
};

export type ClientsWithFormType = { type: ClientFormType; client?: Partial<Client> }[];

export interface ClientDataProps {
  client: Partial<Client>;
  clientFormTypes: ClientFormType[];
  clientPolicyHolderType?: "insurance" | "loan";
}

export const createClientsData = (clientsWithType: ClientsWithFormType) => {
  const clientsData: ClientDataProps[] = [];

  clientsWithType.forEach((clientWithType) => {
    const { client, type } = clientWithType;

    if (!client) {
      return;
    }

    let clientDataIndex = 0;
    const clientData = clientsData.find((clientData, key) => {
      if (clientData.client.id === client.id) {
        clientDataIndex = key;
        return true;
      }
      return false;
    });

    if (clientData) {
      clientsData[clientDataIndex] = {
        ...clientData,
        clientFormTypes: clientData.clientFormTypes.includes(type)
          ? clientData.clientFormTypes
          : [...clientData.clientFormTypes, type],
        clientPolicyHolderType: type === ClientFormType.POLICY_HOLDER ? "insurance" : clientData.clientPolicyHolderType,
      };
    } else {
      clientsData.push({
        client: client,
        clientFormTypes: [type],
        clientPolicyHolderType: type === ClientFormType.POLICY_HOLDER ? "insurance" : undefined,
      });
    }
  });

  return clientsData;
};

export const formatClientName = (client?: ClientBase) => {
  return client ? `${client.aggregatedName} (${client.identifier})` : undefined;
};

export const convertClientAgreementToCreateUpdateObject = (agreement: ClientAgreement): CreateUpdateClientAgreement => {
  const { id, createdAt, updatedAt, ...data } = agreement;

  return data;
};

export const CLIENT_DETAIL_TAB = {
  CLIENT: "client",
  PORTFOLIO: "portfolio",
  HISTORY: "history",
  ACCOUNTS: "accounts",
  ATTACHMENTS: "attachments",
  DOCUMENTS_TO_SIGN: "documents-sign",
  AGREEMENTS: "agreements",
  ASSIGNED_AGENTS: "assigned-agents",
};

export const ADD_ACCOUNT_QUERY_KEY = "addAccount";

const isPartialClientObject = (client: Partial<Client | CreateUpdateClient>): client is Partial<Client> => {
  return "id" in client || "managedBy" in client;
};

type MissingFields =
  | "phone"
  | "email"
  | "birthPlace"
  | "bankSwift"
  | "identityCardNumber"
  | "iban"
  | "frontSideIdentityCard"
  | "backSideIdentityCard";

export const checkLunaClientFields = (client: Client, clientAttachments: ClientAttachment[]) => {
  const missionLunaImportantFields: MissingFields[] = [];

  if (!isNaturalClient(client)) {
    return missionLunaImportantFields;
  }

  if (
    !client.contacts.find((contact) => contact.type === ContactType.MAIN_EMAIL || contact.type === ContactType.EMAIL)
  ) {
    missionLunaImportantFields.push("email");
  }

  if (
    !client.contacts.find(
      (contact) => contact.type === ContactType.MAIN_PHONE_NUMBER || contact.type === ContactType.PHONE_NUMBER,
    )
  ) {
    missionLunaImportantFields.push("phone");
  }

  if (!client.birthPlace) {
    missionLunaImportantFields.push("birthPlace");
  }

  if (!client.identityCardNumber) {
    missionLunaImportantFields.push("identityCardNumber");
  }

  if (!client.bankAccountNumber) {
    missionLunaImportantFields.push("iban");
  }

  if (!client.bankSwift) {
    missionLunaImportantFields.push("bankSwift");
  }

  if (!clientAttachments.find((attachment) => attachment.type === ClientAttachmentType.FRONT_ID_CARD)) {
    missionLunaImportantFields.push("frontSideIdentityCard");
  }

  if (!clientAttachments.find((attachment) => attachment.type === ClientAttachmentType.BACK_ID_CARD)) {
    missionLunaImportantFields.push("backSideIdentityCard");
  }

  return missionLunaImportantFields;
};

export const hasMoreThanOneMainContacts = (client: Client) => {
  return (
    client.contacts.filter((contact) => contact.type === ContactType.MAIN_PHONE_NUMBER).length > 1 ||
    client.contacts.filter((contact) => contact.type === ContactType.MAIN_EMAIL).length > 1
  );
};

export const hasMoreThanOneBasicContacts = (client: Client) => {
  return (
    client.contacts.filter((contact) => contact.type === ContactType.PHONE_NUMBER).length > 1 ||
    client.contacts.filter((contact) => contact.type === ContactType.EMAIL).length > 1
  );
};
